import { DatePicker } from '@kula-ai/components';
import type {
  DatePickerTriggerProps,
  DatePreset,
  DateRange,
  RangeDatePickerProps,
} from '@kula-ai/components';
import { useDateFormat } from '@/hooks';
import {
  DATE_PANEL_CLASS,
  formatRange,
  fromRanges,
  toRanges,
} from './dateRangeValue';
import type { DateRangeValue } from './dateRangeValue';

/** What a trigger is given: kula's own props plus the formatted range. */
export type DateRangeTriggerProps = DatePickerTriggerProps & {
  /** "01/09/2026 - 11/09/2026" in the account's format, or '' when unset. */
  rangeLabel: string;
};

export type DateRangePickerProps = {
  value?: DateRangeValue;
  /** Fires on confirm, not on every calendar click. */
  onChange: (value: DateRangeValue) => void;
  renderTrigger: (props: DateRangeTriggerProps) => React.ReactNode;
  presets?: DatePreset[];
  /** Controlled open state; leave unset for an uncontrolled picker. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Grey out individual days that simple bounds cannot express. */
  disabledDay?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  /** First month shown when nothing is selected yet. */
  shownDate?: Date;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Pass a dialog's own node when a focus trap would swallow a body portal. */
  portalTarget?: HTMLElement | null;
  /** Sunday by default, which is what every picker in the app rendered. */
  weekStartsOn?: 0 | 1;
  visibleMonths?: 1 | 2;
  /** kula's own "Skip weekends" checkbox in the panel footer. */
  showSkipWeekends?: boolean;
  /** On the trigger wrapper. */
  className?: string;
  /** On the panel, alongside the marker class. */
  calendarClassName?: string;
  /** Escape hatch onto react-date-range; spreads last, so it wins. */
  rangePickerProps?: RangeDatePickerProps['rangePickerProps'];
};

/**
 * The app's date range picker: kula's range mode, plus the two things it does
 * not carry — the account's display format and the `{ from, to }` day-string
 * shape the APIs use. Form binding and any per-surface rules stay with the
 * caller; this only draws the panel.
 */
export const DateRangePicker = ({
  value,
  onChange,
  renderTrigger,
  presets,
  open,
  onOpenChange,
  disabledDay,
  minDate,
  maxDate,
  shownDate,
  confirmLabel = 'Apply',
  cancelLabel = 'Cancel',
  portalTarget,
  weekStartsOn = 0,
  visibleMonths = 2,
  showSkipWeekends,
  className,
  calendarClassName,
  rangePickerProps,
}: DateRangePickerProps) => {
  const { dateFnsFormat } = useDateFormat();
  const ranges = toRanges(value);
  const rangeLabel = formatRange(value, dateFnsFormat);

  return (
    <DatePicker
      mode="range"
      ranges={ranges}
      onRangeChange={(next: DateRange[]) => onChange(fromRanges(next))}
      {...(open === undefined ? {} : { open })}
      onOpenChange={onOpenChange}
      showPresets={Boolean(presets?.length)}
      presets={presets}
      visibleMonths={visibleMonths}
      showSkipWeekends={showSkipWeekends}
      dateFormat={dateFnsFormat}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      portalTarget={portalTarget}
      className={className}
      calendarClassName={[DATE_PANEL_CLASS, calendarClassName]
        .filter(Boolean)
        .join(' ')}
      rangePickerProps={{
        // kula's range panel hardcodes Monday over its own locale, and the
        // spread lands after that literal, so this is what takes effect.
        weekStartsOn,
        ...(minDate ? { minDate } : {}),
        ...(maxDate ? { maxDate } : {}),
        ...(shownDate && !value?.from ? { shownDate } : {}),
        ...(disabledDay ? { disabledDay } : {}),
        ...rangePickerProps,
      }}
      renderTrigger={(props) => renderTrigger({ ...props, rangeLabel })}
    />
  );
};
